import type { AcceptedContact } from '@ben/shared/utils/contacts';
import { IS_EA } from '@ben/shared/constants';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface UseNavigateProfileDependentsParams {
  requiredContactFields?: string[];
  acceptedContactTypes?: AcceptedContact[];
}

export const useNavigateProfileDependents = ({
  requiredContactFields,
  acceptedContactTypes,
}: UseNavigateProfileDependentsParams) => {
  const navigate = useNavigate();
  const { employeeId } = useParams();

  const queryString = useMemo(() => {
    const requiredParameter = requiredContactFields?.length
      ? `required=${requiredContactFields.join(',')}`
      : '';

    const acceptedParameter = acceptedContactTypes?.length
      ? `accepted=${acceptedContactTypes.join(',')}`
      : '';

    const queryParts = [requiredParameter, acceptedParameter].filter(Boolean);
    return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  }, [requiredContactFields, acceptedContactTypes]);

  const navigateAddNewDependent = useCallback(() => {
    if (IS_EA) {
      navigate(`/profile/dependents/create${queryString}`);
    } else {
      navigate(`/people/${employeeId}/dependents/create${queryString}`);
    }
  }, [navigate, employeeId, queryString]);

  const navigateEditDependent = useCallback(
    (contactId: string) => {
      if (IS_EA) {
        navigate(`/profile/dependents/${contactId}/edit${queryString}`);
      } else {
        navigate(
          `/people/${employeeId}/dependents/${contactId}/edit${queryString}`
        );
      }
    },
    [navigate, employeeId, queryString]
  );

  return {
    navigateAddNewDependent,
    navigateEditDependent,
  };
};
